import { z } from 'zod';
import { callWindowSchema, communicationChannelSchema } from '../common/types';

/**
 * Patient demographics cached from EHR.
 */
export const patientDemographicsSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  middleName: z.string().optional(),
  dateOfBirth: z.string(),
  gender: z.enum(['male', 'female', 'other', 'unknown']).optional(),
  preferredLanguage: z.string().optional(),
  ethnicity: z.string().optional(),
  ethnicBackground: z.string().optional(),
  race: z.string().optional(),
  religion: z.string().optional(),
});

export type PatientDemographics = z.infer<typeof patientDemographicsSchema>;

/**
 * Patient contact information.
 */
export const patientContactSchema = z.object({
  phone: z.string().optional(),
  email: z.string().optional(),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postalCode: z.string().optional(),
      country: z.string().optional(),
    })
    .optional(),
});

export type PatientContact = z.infer<typeof patientContactSchema>;

/**
 * Insurance coverage info (FHIR Coverage-aligned).
 */
export const insuranceCoverageSchema = z.object({
  status: z.enum(['active', 'cancelled', 'draft']).optional(),
  payerId: z.string().optional(),
  payerName: z.string().optional(),
  subscriberId: z.string().optional(),
  subscriberLastName: z.string().optional(),
  subscriberFirstName: z.string().optional(),
  subscriberMiddleName: z.string().optional(),
  relationship: z.string().optional(),
  memberId: z.string().optional(),
  groupNumber: z.string().optional(),
  groupName: z.string().optional(),
  planName: z.string().optional(),
  planType: z.string().optional(),
  coverageStart: z.string().optional(),
  coverageEnd: z.string().optional(),
  network: z.string().optional(),
  copay: z.number().optional(),
  deductible: z.number().optional(),
  deductibleMet: z.number().optional(),
  order: z.number().optional(),
});

/**
 * Patient insurance information.
 */
export const patientInsuranceSchema = z.object({
  primary: insuranceCoverageSchema.optional(),
  secondary: insuranceCoverageSchema.optional(),
  tertiary: insuranceCoverageSchema.optional(),
});

export type PatientInsurance = z.infer<typeof patientInsuranceSchema>;

/**
 * Care team member.
 */
export const careTeamMemberSchema = z.object({
  ehrProviderId: z.string().optional(),
  name: z.string(),
  role: z.string().optional(),
  specialty: z.string().optional(),
  phone: z.string().optional(),
  npi: z.string().optional(),
});

export type CareTeamMember = z.infer<typeof careTeamMemberSchema>;

/**
 * Patient care team.
 */
export const patientCareTeamSchema = z.object({
  primaryCareProvider: careTeamMemberSchema.optional(),
  members: z.array(careTeamMemberSchema),
});

export type PatientCareTeam = z.infer<typeof patientCareTeamSchema>;

/**
 * Patient medication.
 */
export const patientMedicationSchema = z.object({
  name: z.string(),
  dosage: z.string().optional(),
  frequency: z.string().optional(),
  prescribedDate: z.string().optional(),
  status: z.enum(['active', 'stopped', 'completed']).optional(),
  prescribedBy: z.string().optional(),
});

export type PatientMedication = z.infer<typeof patientMedicationSchema>;

/**
 * Cached data with fetch timestamps.
 */
export const patientCacheDataSchema = z.object({
  demographics: z
    .object({
      data: patientDemographicsSchema,
      fetchedAt: z.string(),
    })
    .optional(),
  contact: z
    .object({
      data: patientContactSchema,
      fetchedAt: z.string(),
    })
    .optional(),
  insurance: z
    .object({
      data: patientInsuranceSchema,
      fetchedAt: z.string(),
    })
    .optional(),
  careTeam: z
    .object({
      data: patientCareTeamSchema,
      fetchedAt: z.string(),
    })
    .optional(),
  medications: z
    .object({
      data: z.array(patientMedicationSchema),
      fetchedAt: z.string(),
    })
    .optional(),
});

export type PatientCacheData = z.infer<typeof patientCacheDataSchema>;

/**
 * Patient response schema.
 */
export const patientResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  mrn: z.string().nullable(),
  ehrPatientId: z.string().nullable(),
  phone: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string().nullable(),
  preferredName: z.string().nullable().optional(),
  preferredSex: z.string().nullable().optional(),
  email: z.string().nullable(),
  /**
   * Dial-ready E.164 numbers derived from `phone` + the patient_phones table
   * (deduped, primary first). Populated ONLY on the patient-detail response;
   * other endpoints (search/lookup/create) omit it, so it's optional — the
   * client casts responses without re-parsing, so a `.default([])` would not
   * actually run on the wire and would lie about presence. Consumers guard with
   * `?? []`. The call button enables when this is non-empty and dials the first
   * entry, offering the rest as alternatives.
   */
  dialablePhones: z.array(z.string()).optional(),
  cachedData: patientCacheDataSchema.nullable(),
  /** Last SUCCESSFUL save from the EHR. */
  lastEhrSyncAt: z.string().datetime().nullable(),
  /**
   * The latest EHR sync of this patient failed, and when (ADR-284). Cleared by
   * every successful save, so set means the chart may be out of date. Show the
   * error with `ehrErrorMessage(ehrSyncErrorCode, ehrSyncError)`.
   */
  ehrSyncFailedAt: z.string().datetime().nullable().optional(),
  ehrSyncError: z.string().nullable().optional(),
  ehrSyncErrorCode: z.string().nullable().optional(),
  flagged: z.number().int(),
  rowStatus: z.number().int(),
  createdBy: z.string().uuid().nullable(),
  updatedBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
});

export type PatientResponse = z.infer<typeof patientResponseSchema>;

/**
 * Create patient request schema.
 */
export const createPatientSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: z.string().optional(),
  preferredName: z.string().optional(),
  preferredSex: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Invalid email format').optional(),
  mrn: z.string().optional(),
  ehrPatientId: z.string().optional(),
});

export type CreatePatientRequest = z.infer<typeof createPatientSchema>;

/** Two-letter USPS state code. */
export const US_STATE_PATTERN = /^[A-Za-z]{2}$/;
/** 5-digit ZIP or ZIP+4. */
export const US_ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

type AddressParts = {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
};

/**
 * The required address parts left blank once any part of an address is given
 * (none when no address was entered). An address is all or nothing: the EHR
 * stores street, city, state and zip separately, and half an address is worse
 * than none.
 */
export function incompleteAddressFields(
  v: AddressParts
): Array<'address1' | 'city' | 'state' | 'zip'> {
  const required = ['address1', 'city', 'state', 'zip'] as const;
  const given = required.some((f) => v[f]) || Boolean(v.address2);
  return given ? required.filter((f) => !v[f]) : [];
}

/**
 * Create-patient-in-EHR request (ADR-089). Creates the patient in the org's EHR
 * (system of record) and links the local row. `locationId` resolves to the EHR
 * department/location the patient is created under. Used by the Patients page's
 * New patient dialog.
 *
 * The server looks for the person in VagusX and the EHR first and refuses with a
 * 409 when it finds them (see {@link existingPatientConflictSchema} and
 * {@link possibleDuplicateConflictSchema}). `allowPossibleDuplicate` lets a user
 * who has seen the possible matches create anyway; a definite match is always
 * refused.
 */
export const createPatientInEhrSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    dateOfBirth: z.string().min(1, 'Date of birth is required'),
    locationId: z.string().uuid(),
    phone: z.string().optional(),
    email: z.string().email('Invalid email format').optional(),
    gender: z.enum(['male', 'female', 'other', 'unknown']).optional(),
    address1: z.string().trim().min(1).optional(),
    address2: z.string().trim().min(1).optional(),
    city: z.string().trim().min(1).optional(),
    state: z
      .string()
      .trim()
      .regex(US_STATE_PATTERN, 'Use the two-letter state code')
      .transform((v) => v.toUpperCase())
      .optional(),
    zip: z.string().trim().regex(US_ZIP_PATTERN, 'Use a 5-digit ZIP or ZIP+4').optional(),
    allowPossibleDuplicate: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    for (const field of incompleteAddressFields(v)) {
      ctx.addIssue({ code: 'custom', path: [field], message: 'Complete the address' });
    }
  });

export type CreatePatientInEhrRequest = z.infer<typeof createPatientInEhrSchema>;

/** A patient the create collided with — enough to show them and open their record. */
export const conflictPatientSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string().nullable(),
  /** Null when the match is a VagusX-only patient with no EHR chart yet. */
  ehrPatientId: z.string().nullable(),
});

/** `details` of a 409 `ALREADY_EXISTS` from a create-in-EHR: a definite match. */
export const existingPatientConflictSchema = z.object({
  existingPatient: conflictPatientSchema,
});

/**
 * A record that may be the same person: an EHR chart the EHR search flagged, or a
 * VagusX patient the local matcher flagged (e.g. a twin or household member sharing
 * last name, DOB and phone).
 */
export const possibleMatchSchema = z.object({
  /** The VagusX patient, when there is one. */
  patientId: z.string().uuid().nullable(),
  /** The EHR chart, when there is one. */
  ehrPatientId: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string().nullable(),
});

/** `details` of a 409 `POSSIBLE_DUPLICATE` from a create-in-EHR. */
export const possibleDuplicateConflictSchema = z.object({
  possibleMatches: z.array(possibleMatchSchema).min(1),
});

export type ConflictPatient = z.infer<typeof conflictPatientSchema>;
export type PossibleMatch = z.infer<typeof possibleMatchSchema>;

/**
 * Update patient request schema.
 */
export const updatePatientSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  dateOfBirth: z.string().optional(),
  preferredName: z.string().nullable().optional(),
  preferredSex: z.string().nullable().optional(),
  phone: z.string().optional(),
  email: z.string().email('Invalid email format').optional(),
  mrn: z.string().optional(),
  ehrPatientId: z.string().optional(),
});

export type UpdatePatientRequest = z.infer<typeof updatePatientSchema>;

/**
 * Patient lookup request schema.
 */
export const patientLookupSchema = z.object({
  phone: z.string().optional(),
  mrn: z.string().optional(),
  lastName: z.string().optional(),
  dateOfBirth: z.string().optional(),
});

export type PatientLookupRequest = z.infer<typeof patientLookupSchema>;

/**
 * Patient flag status response schema.
 */
export const patientFlagStatusSchema = z.object({
  flagged: z.boolean(),
  flaggedInteractionCount: z.number().int(),
  canSafelyUnflag: z.boolean(),
});

export type PatientFlagStatus = z.infer<typeof patientFlagStatusSchema>;

// =============================================================================
// Contact Preferences
// =============================================================================

export const contactPreferencesSchema = z.object({
  // Ordered by preference - first is highest priority
  preferredChannels: z.array(communicationChannelSchema).optional(),

  // Best times to call (same structure as agent config)
  callWindows: z.array(callWindowSchema).optional(),

  // Hard stop - never contact this patient
  doNotContact: z.boolean().default(false),
});

export type ContactPreferences = z.infer<typeof contactPreferencesSchema>;

// =============================================================================
// Caller identity (inbound voice)
// =============================================================================

/**
 * What a verified caller carries forward into scheduling. Deliberately the
 * MINIMUM: `id` is what the booking call keys on, the name is what the agent
 * addresses them by, and the rest is corroboration the agent already had. No
 * MRN, no insurance, no cached chart data — a voice agent has no use for them,
 * and every field crossing this boundary is a field that can be spoken aloud.
 */
export const identifiedPatientSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string().nullable(),
  phone: z.string().nullable(),
});

export type IdentifiedPatient = z.infer<typeof identifiedPatientSchema>;

/**
 * The verdict for an inbound caller.
 *
 * `patient` is non-null ONLY when `outcome` is `match`. That is the point of the
 * shape: an unidentified caller is not entitled to learn that a chart exists,
 * how many people share their name, or which detail missed — all PHI about
 * people who are probably not on the phone. A response that cannot carry
 * candidates cannot leak them, whatever a prompt asks for.
 *
 * Not a boolean-flag shape (`{ found: true, ... }`) per the repo's API rules —
 * three outcomes, because "nobody matched" and "too many matched" call for
 * different next questions even though today both just re-ask.
 */
export const identifyCallerResponseSchema = z.object({
  outcome: z.enum(['match', 'ambiguous', 'no_match']),
  patient: identifiedPatientSchema.nullable(),
});

export type IdentifyCallerResponse = z.infer<typeof identifyCallerResponseSchema>;

/**
 * Request for the chart facts an inbound call reads back in `confirm_details`
 * (ADR-256). Keyed on `patientId`, which only a VERIFIED identity produces —
 * the voice runtime reaches this after `/patients/identify` matched, never on
 * caller ID.
 */
export const callChartFactsLookupSchema = z.object({
  organizationId: z.string().uuid(),
  patientId: z.string().uuid(),
});

export type CallChartFactsLookup = z.infer<typeof callChartFactsLookupSchema>;

/**
 * The two chart facts, ALREADY REDUCED TO WHAT IS SPEAKABLE: the street line
 * only (no city, state or zip) and the insurance carrier only (no plan detail,
 * no member id) — the reduction `formatChartFieldsForCall` applies for the
 * outbound dial. `null` per field means "not on file".
 */
export const callChartFactsSchema = z.object({
  addressLine: z.string().nullable(),
  insurance: z.string().nullable(),
});

export type CallChartFacts = z.infer<typeof callChartFactsSchema>;

/**
 * `chart` is null when no patient with that id exists in the organization —
 * not a boolean-flag shape, per the repo's API rules.
 */
export const callChartFactsResponseSchema = z.object({
  chart: callChartFactsSchema.nullable(),
});

export type CallChartFactsResponse = z.infer<typeof callChartFactsResponseSchema>;
